import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'
import { fetchJSON, getDefaultRequestConfig, getElement } from '@bao/utils'

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
export const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM, {
        form,
        formData,
        state,
      })

      const cart = await this.cartQuery()

      const changedState = {
        ...cart,
        sections: state.sections,
      }

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state: changedState })

      return state
    } catch (err) {
      this.handleAddToCartError(err)

      const pushCartSections = sectionRenderer.pushCartSections

      if (!pushCartSections || !pushCartSections.length) return

      sectionRenderer
        .fetch(pushCartSections.map((s) => s.section))
        .then((htmlSections) => {
          const updatedSections = pushCartSections.map((section) => ({
            ...section,
            html: htmlSections[section.section],
          }))

          sectionRenderer.renderMultiple(updatedSections)
        })
        .catch((err) => {
          console.error('Failed to fetch cart sections:', err)
        })
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_ADD, {
      items,
      attributes,
      form,
    })

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_ADD, {
        items,
        state,
        form,
      })

      const cart = await this.cartQuery()

      const changedState = {
        ...cart,
        sections: state.sections,
      }

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state: changedState })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY, {
      key,
      quantity,
    })
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM, { key })

    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM, {
      keys,
    })

    const body = {
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async clearCart ({
    sections = sectionRenderer.pushCartSections.map((s) => s.section),
    sectionsUrl = DEFAULT_SECTIONS_URL,
  } = {}) {
    const body = {}

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    const state = await this.cartClear(body)

    eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state })

    return state
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_ITEM_PROPERTIES_UPDATE, {
        properties,
      })

      const state = await this.cartChange(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_ITEM_PROPERTIES_UPDATE, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES, {
      attributes,
    })

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES, {
        attributes,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Refresh push cart sections from the server and notify listeners
   *
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object|null>}
   */
  async refreshPushCart ({
    sections = sectionRenderer.pushCartSections.map((s) => s.section),
    sectionsUrl = DEFAULT_SECTIONS_URL,
  } = {}) {
    try {
      if (!sections.length) return null

      const htmlSections = await sectionRenderer.fetch(sections, sectionsUrl)

      const updatedSections = sectionRenderer.pushCartSections
        .map((section) => ({
          ...section,
          html: htmlSections[section.section],
        }))
        .filter((section) => section.html)

      if (updatedSections.length) {
        sectionRenderer.renderMultiple(updatedSections)
      }

      const state = { sections: htmlSections }

      return state
    } catch (err) {
      console.error('Failed to refresh push cart:', err)
      return null
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    eventBus.dispatchEvent(EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      eventBus.dispatchEvent(EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs we want to pass to /cart/clear.js
   *
   * @returns {Promise<Object>}
   */
  cartClear (body) {
    return fetchJSON(`${window.Shopify.routes.root}cart/clear.js`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  cartQuery () {
    return fetchJSON(`${window.routes.cartUrl}`, {
      ...getDefaultRequestConfig(),
    })
  }

  /**
   * Update cart discount codes
   *
   * @param {string} discount Comma-separated discount codes
   * @param {BaoCartSectionOptions & {attemptedCode?: string, suppressDiscountValidation?: boolean}} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartDiscount (
    discount,
    {
      attemptedCode = null,
      suppressDiscountValidation = null,
      sections = null,
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_CART_DISCOUNT, {
      discount,
    })

    const hasCart = !!document.getElementById('template-cart')
    const hasPushCart = !!document.getElementById('push-cart')

    const sectionDefs = hasCart
      ? sectionRenderer?.cartSections || []
      : hasPushCart
        ? sectionRenderer?.pushCartSections || []
        : []

    const resolvedSections = Array.isArray(sections)
      ? sections
      : sectionDefs.map((s) => s.section).filter(Boolean)

    const body = { discount }

    if (resolvedSections.length) {
      body.sections = resolvedSections
      if (sectionsUrl) body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      if (state?.sections && sectionDefs.length) {
        const updatedSections = sectionDefs
          .map((def) => ({
            ...def,
            html: state.sections[def.section],
          }))
          .filter((s) => s.html)

        if (updatedSections.length) {
          sectionRenderer.renderMultiple(updatedSections)
        }
      }

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_CART_DISCOUNT, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, { state })

      if (!suppressDiscountValidation) {
        this.checkValidCode(state, attemptedCode)
      }

      return state
    } catch (e) {
      console.error(e)
      const discountCodeForm = getElement('discount-code')
      if (discountCodeForm) {
        discountCodeForm.handleResponse({ applicable: false })
      }
    }
  }

  /**
   * Check if discount code is valid and notify discount code form
   *
   * @param {Object} state Cart state
   *
   * @returns {void}
   */
  checkValidCode (state, attemptedCode = null) {
    const cart = state.cart || state
    const discountCodes = cart.discount_codes || []

    const discountApplications =
      cart.cart_level_discount_applications || cart.discount_applications || []

    const discountCodeForm = getElement('discount-code')
    if (!discountCodeForm) return

    if (attemptedCode) {
      const attemptedLower = String(attemptedCode).trim().toLowerCase()

      const matched = discountCodes.find((dc) => {
        const code = (dc?.code || '').toString().trim().toLowerCase()
        return code === attemptedLower
      })

      const applicable = matched?.applicable === true

      discountCodeForm.handleResponse({
        applicable,
        ...(matched || {}),
        code: attemptedCode,
      })

      return
    }

    const hasDiscount =
      discountCodes.length > 0 || discountApplications.length > 0

    const lastCode = discountApplications[0] || discountCodes[0]

    if (hasDiscount && lastCode) {
      discountCodeForm.handleResponse({ applicable: true, ...lastCode })
    } else {
      discountCodeForm.handleResponse({ applicable: false })
    }
  }
}

export const Cart = new BaoCart()

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return

  Cart.refreshPushCart()
})
